import { useCallback } from 'react';
import type { GroupSetSchema } from '../types';
import type { Section } from '../models/Section';
import { useFormContext } from '../context/FormContext';

export interface UseGroupSetResult {
  count: number;
  add: () => void;
  remove: () => void;
  min: number;
  max: number;
}

export function useGroupSet(groupSetSchema: GroupSetSchema, section: Section): UseGroupSetResult {
  const { groupSetCounts, setGroupSetCount, formService, scrollService, forceUpdate } =
    useFormContext();

  const min = groupSetSchema.min ?? 0;
  const max = groupSetSchema.max ?? 5;
  const count =
    groupSetCounts[groupSetSchema.groupSetId] ??
    formService.getPreloadedNumOfGroups(groupSetSchema.group.id, min || 1);

  const syncCount = useCallback<(newCount: number) => void>(
    (newCount: number) => {
      setGroupSetCount(groupSetSchema.groupSetId, newCount);
      formService.syncGroupSet(groupSetSchema, newCount, section);
      forceUpdate();

      setTimeout(() => {
        const target = document.getElementById(groupSetSchema.groupSetId);
        const groups = target?.querySelectorAll('.group');
        if (groups && groups.length > 0) {
          scrollService.scrollElementTo(groups[groups.length - 1], 100);
        }
      }, 100);
    },
    [groupSetSchema, section, setGroupSetCount, formService, forceUpdate, scrollService]
  );

  const add = useCallback<() => void>(() => {
    if (count < max) {
      syncCount(count + 1);
    }
  }, [count, max, syncCount]);

  const remove = useCallback<() => void>(() => {
    if (count > min) {
      syncCount(count - 1);
    }
  }, [count, min, syncCount]);

  return { count, add, remove, min, max };
}
